'use client';

import { forwardRef, createContext, useContext } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

// Context for settings display options (e.g., hide icons in compact mode)
interface SettingsDisplayContextValue {
  hideIcons?: boolean;
}

const SettingsDisplayContext = createContext<SettingsDisplayContextValue>({});

export function SettingsDisplayProvider({
  children,
  hideIcons = false,
}: {
  children: React.ReactNode;
  hideIcons?: boolean;
}) {
  return (
    <SettingsDisplayContext.Provider value={{ hideIcons }}>
      {children}
    </SettingsDisplayContext.Provider>
  );
}

export function useSettingsDisplay() {
  return useContext(SettingsDisplayContext);
}

interface SettingsItemProps {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  subtitle?: string;
  hasChanges?: boolean;
  onClick?: () => void;
  href?: string;
  className?: string;
  /** Show chevron on the right (default: true) */
  showChevron?: boolean;
  /** Position in list for border-radius (auto-detected if not provided) */
  position?: 'first' | 'middle' | 'last' | 'only';
  /** Destructive action styling (red text) */
  destructive?: boolean;
}

/**
 * Settings item component - iOS/Android style menu row
 * Used inside SettingsCard for individual settings options
 */
export const SettingsItem = forwardRef<HTMLDivElement, SettingsItemProps>(
  (
    {
      icon: Icon,
      title,
      subtitle,
      hasChanges = false,
      onClick,
      className,
      showChevron = true,
      destructive = false,
    },
    ref
  ) => {
    const { hideIcons } = useSettingsDisplay();

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3 py-3 px-5 hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer',
          className
        )}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        {!hideIcons && Icon && (
          <Icon
            className={cn(
              'h-5 w-5',
              destructive ? 'text-destructive' : 'text-primary'
            )}
            strokeWidth={1.5}
          />
        )}

        <span
          className={cn(
            'text-list-item-title flex-1',
            destructive ? 'text-destructive' : 'text-foreground'
          )}
        >
          {title}
        </span>

        {hasChanges && (
          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
        )}

        {subtitle && (
          <span className="text-list-item-subtitle text-muted-foreground max-w-[100px] truncate">
            {subtitle}
          </span>
        )}

        {showChevron && (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
        )}
      </div>
    );
  }
);

SettingsItem.displayName = 'SettingsItem';
